import { css } from "@codemirror/lang-css";
import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import PropTypes from "prop-types";
import { useCallback, useMemo, useRef } from "react";
import { TEST_IDS } from "../../../constants/testIds";
import { Skeleton } from "../../ui/skeleton";

// Clean up content that may be wrapped in markdown code blocks
const cleanMarkdownContent = (content) => {
  if (!content || typeof content !== "string") return content;

  // Remove markdown code block wrapper if present
  const markdownBlockMatch = content.match(/^```markdown\s*\n([\s\S]*?)\n```$/);
  if (markdownBlockMatch) {
    return markdownBlockMatch[1].trim();
  }

  // Remove any other code block wrapper if present
  const codeBlockMatch = content.match(/^```[\w]*\s*\n([\s\S]*?)\n```$/);
  if (codeBlockMatch) {
    return codeBlockMatch[1].trim();
  }

  return content;
};

// Helper function to extract actual code content from BlockNote JSON or other formats
function extractCodeContent(content) {
  if (!content) return "";

  // If content is a string, first clean any markdown wrapping
  if (typeof content === "string") {
    // Clean markdown wrapping first
    const cleanedContent = cleanMarkdownContent(content);

    // Check if it's BlockNote JSON
    if (cleanedContent.startsWith("[") || cleanedContent.startsWith("{")) {
      try {
        const parsed = JSON.parse(cleanedContent);

        // Handle array of blocks (BlockNote format)
        if (Array.isArray(parsed)) {
          // Look for code blocks first
          const codeBlocks = parsed.filter(
            (block) => block.type === "codeBlock"
          );
          if (codeBlocks.length > 0) {
            // Return the content of the first code block
            const codeBlock = codeBlocks[0];
            if (
              codeBlock.content &&
              codeBlock.content[0] &&
              codeBlock.content[0].text
            ) {
              return cleanCodeContent(codeBlock.content[0].text);
            }
          }

          // If no code blocks, concatenate all text content
          let extractedText = "";
          parsed.forEach((block) => {
            if (block.content && Array.isArray(block.content)) {
              block.content.forEach((item) => {
                if (item.type === "text" && item.text) {
                  extractedText += item.text + "\n";
                }
              });
            }
          });

          return cleanCodeContent(extractedText.trim());
        }

        // Handle single block object
        if (
          parsed.type === "codeBlock" &&
          parsed.content &&
          parsed.content[0]
        ) {
          return cleanCodeContent(parsed.content[0].text || "");
        }
      } catch {
        // Not JSON, return cleaned content
        return cleanCodeContent(cleanedContent);
      }
    }

    // Return cleaned string content
    return cleanCodeContent(cleanedContent);
  }

  // If content is already an object/array, try to extract from it
  if (Array.isArray(content)) {
    const codeBlocks = content.filter((block) => block.type === "codeBlock");
    if (
      codeBlocks.length > 0 &&
      codeBlocks[0].content &&
      codeBlocks[0].content[0]
    ) {
      return cleanCodeContent(codeBlocks[0].content[0].text || "");
    }

    // Extract all text content
    let extractedText = "";
    content.forEach((block) => {
      if (block.content && Array.isArray(block.content)) {
        block.content.forEach((item) => {
          if (item.type === "text" && item.text) {
            extractedText += item.text + "\n";
          }
        });
      }
    });

    return cleanCodeContent(extractedText.trim());
  }

  return cleanCodeContent(String(content));
}

// Helper function to clean code content by removing markdown artifacts
function cleanCodeContent(content) {
  if (!content || typeof content !== "string") return "";

  let cleaned = content;

  // Remove code fence tags (```python, ```javascript, etc.)
  cleaned = cleaned.replace(/^```[\w]*\s*\n?/gm, "");
  cleaned = cleaned.replace(/\n?```\s*$/gm, "");

  // Remove leading/trailing whitespace
  cleaned = cleaned.trim();

  return cleaned;
}

// Helper function to detect if content is markdown
function isMarkdownContent(content) {
  if (!content || typeof content !== "string") return false;

  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s+/m, // Headers
    /^\*\s+/m, // Bullet lists
    /^\d+\.\s+/m, // Numbered lists
    /^>\s+/m, // Blockquotes
    /```[\s\S]*?```/, // Code blocks
    /\*\*.*?\*\*/, // Bold text
    /\*.*?\*/, // Italic text
    /\[.*?\]\(.*?\)/, // Links
    /`.*?`/, // Inline code
    /^\|.*\|/m, // Tables
    /^[-*_]{3,}$/m, // Horizontal rules
  ];

  return markdownPatterns.some((pattern) => pattern.test(content));
}

// Convert markdown content to a more readable format for code editor
function _processMarkdownForCodeEditor(content) {
  if (!isMarkdownContent(content)) {
    return content;
  }

  // For code editor, we'll preserve the markdown as-is but add a comment at the top
  const header = `// Markdown content detected - you can edit this as code or switch to text editor for better markdown editing\n\n`;
  return header + content;
}

// Light theme for CodeMirror
const lightTheme = EditorView.theme({
  "&": {
    fontSize: "14px",
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
    backgroundColor: "#FAFAFA",
    color: "#1a1a1a",
  },
  ".cm-content": {
    padding: "16px",
    minHeight: "calc(100vh - 64px)",
    backgroundColor: "#FAFAFA",
    color: "#1a1a1a",
  },
  ".cm-focused": {
    outline: "none",
  },
  ".cm-editor": {
    minHeight: "calc(100vh - 64px)",
    backgroundColor: "#FAFAFA",
  },
  ".cm-scroller": {
    minHeight: "calc(100vh - 64px)",
    backgroundColor: "#FAFAFA",
  },
  ".cm-gutters": {
    backgroundColor: "#FAFAFA",
    color: "#666666",
    border: "none",
  },
  ".cm-activeLineGutter": {
    backgroundColor: "#F0F0F0",
  },
  ".cm-activeLine": {
    backgroundColor: "#F0F0F0",
  },
  ".cm-selectionBackground": {
    backgroundColor: "#E0E0E0 !important",
  },
  ".cm-cursor": {
    borderLeft: "1px solid #1a1a1a",
  },
  ".cm-line": {
    color: "#1a1a1a",
  },
});

const CodeEditor = ({
  content = "",
  language = "javascript",
  onChange,
  readOnly = false,
}) => {
  const isUserEditingRef = useRef(false);

  // Extract actual code content from BlockNote or other formats
  const extractedContent = useMemo(() => {
    return extractCodeContent(content);
  }, [content]);

  // Check if we have meaningful content to display
  const hasContent = useMemo(() => {
    return extractedContent && extractedContent.trim().length > 0;
  }, [extractedContent]);

  // Process content to handle markdown if detected
  const processedContent = useMemo(() => {
    if (!extractedContent) return "";

    // Return the extracted content directly
    return extractedContent;
  }, [extractedContent]);

  // Normalize language to supported ones (JavaScript and Python primarily)
  const normalizedLanguage = useMemo(() => {
    const lang = language.toLowerCase();
    switch (lang) {
      case "python":
      case "py":
        return "python";
      case "javascript":
      case "js":
      case "jsx":
      case "typescript":
      case "ts":
      case "tsx":
        return "javascript";
      case "html":
        return "html";
      case "css":
        return "css";
      case "json":
        return "json";
      case "markdown":
      case "md":
        // For markdown, use javascript highlighting as fallback
        return "javascript";
      default:
        // Default to javascript for unsupported languages
        return "javascript";
    }
  }, [language]);

  const extensions = useMemo(() => {
    const isDark = document.documentElement.classList.contains("dark");

    const baseExtensions = [
      EditorView.lineWrapping,
      isDark
        ? EditorView.theme({
            "&": {
              fontSize: "14px",
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
              backgroundColor: "#2F2F2F",
              color: "#FFFFFF",
            },
            ".cm-content": {
              padding: "16px",
              minHeight: "calc(100vh - 64px)",
              backgroundColor: "#2F2F2F",
              color: "#FFFFFF",
            },
            ".cm-focused": {
              outline: "none",
            },
            ".cm-editor": {
              minHeight: "calc(100vh - 64px)",
              backgroundColor: "#2F2F2F",
            },
            ".cm-scroller": {
              minHeight: "calc(100vh - 64px)",
              backgroundColor: "#2F2F2F",
            },
            ".cm-gutters": {
              backgroundColor: "#2F2F2F",
              color: "#888888",
              border: "none",
            },
            ".cm-activeLineGutter": {
              backgroundColor: "#3F3F3F",
            },
            ".cm-activeLine": {
              backgroundColor: "#3F3F3F",
            },
            ".cm-selectionBackground": {
              backgroundColor: "#4F4F4F !important",
            },
            ".cm-cursor": {
              borderLeft: "1px solid #FFFFFF",
            },
          })
        : lightTheme,
    ];

    // Add language support based on normalized language
    switch (normalizedLanguage) {
      case "python":
        baseExtensions.push(python());
        break;
      case "html":
        baseExtensions.push(html());
        break;
      case "css":
        baseExtensions.push(css());
        break;
      case "json":
        baseExtensions.push(json());
        break;
      case "javascript":
      default:
        baseExtensions.push(javascript());
        break;
    }

    return baseExtensions;
  }, [normalizedLanguage]);

  const handleChange = useCallback(
    (value) => {
      // Mark that user is editing
      isUserEditingRef.current = true;

      if (onChange) {
        onChange(value);
      }

      // Reset editing flag after a short delay
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
    },
    [onChange]
  );

  // Show skeleton if no meaningful content is available
  if (!hasContent) {
    const isDark = document.documentElement.classList.contains("dark");

    return (
      <div
        className="h-full w-full p-4 space-y-3"
        style={{ backgroundColor: isDark ? "#2F2F2F" : "#FAFAFA" }}
      >
        {/* Code skeleton with monospace appearance */}
        <div className="space-y-2">
          <Skeleton
            className={`h-4 w-48 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-64 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-32 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>

        <div className="space-y-2 mt-6">
          <Skeleton
            className={`h-4 w-56 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-72 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-40 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-64 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>

        <div className="space-y-2 mt-6">
          <Skeleton
            className={`h-4 w-24 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-80 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-48 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>

        <div className="space-y-2 mt-6">
          <Skeleton
            className={`h-4 w-36 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-60 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-44 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-28 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>

        <div className="space-y-2 mt-6">
          <Skeleton
            className={`h-4 w-52 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
          <Skeleton
            className={`h-4 w-68 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
          />
        </div>
      </div>
    );
  }

  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div
      className="h-full w-full overflow-auto"
      data-testid={TEST_IDS.CANVAS_CODE_EDITOR}
      style={{ backgroundColor: isDark ? "#2F2F2F" : "#FAFAFA" }}
    >
      <CodeMirror
        value={processedContent}
        onChange={handleChange}
        extensions={extensions}
        theme={isDark ? oneDark : undefined}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          dropCursor: false,
          allowMultipleSelections: false,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          highlightSelectionMatches: false,
          searchKeymap: true,
        }}
        readOnly={readOnly}
        className="h-full text-sm"
        style={{
          backgroundColor: isDark ? "#2F2F2F" : "#FAFAFA",
          minHeight: "100vh",
        }}
        height="100%"
      />
    </div>
  );
};

CodeEditor.propTypes = {
  content: PropTypes.string,
  language: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default CodeEditor;
